import { DATE_DISPLAY_FORMAT, DISPENSE_INTERVAL_FALLBACK_TZ } from '@/utils/helpers';
import { Box, Stack, Typography } from '@montugroup/design-system';
import { useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import type { TimelinePoint } from './Timeline';
import { Timeline } from './Timeline';
import type { CurrentDispenseWindow, PreviousDispenseWindow } from './unable-to-dispense.types';

const LEGEND_INDICATOR_SIZE = 8;

function DispensingTimelineLegendIndicator({ color }: { color: string }) {
  return (
    <Box
      sx={{
        minWidth: LEGEND_INDICATOR_SIZE,
        minHeight: LEGEND_INDICATOR_SIZE,
        backgroundColor: color,
        borderRadius: '50%',
      }}
    />
  );
}

const parseIntervalDate = (isoDate: string) => {
  const luxonDate = DateTime.fromISO(isoDate, { setZone: true });

  return {
    iso: luxonDate,
    isoString: isoDate,
    formatted: luxonDate.toFormat(DATE_DISPLAY_FORMAT),
  };
};

export function DispensingTimelineLegend() {
  const theme = useTheme();

  return (
    <Stack direction={'row'} gap={14}>
      <Stack direction={'row'} gap={2} justifyContent={'center'} alignItems={'center'}>
        <DispensingTimelineLegendIndicator color={theme.palette.primary.main} />
        <Typography variant="caption" fontWeight={600} textTransform={'uppercase'}>
          Unit Dispensed
        </Typography>
      </Stack>
      <Stack direction={'row'} gap={2} justifyContent={'center'} alignItems={'center'}>
        <DispensingTimelineLegendIndicator color={theme.palette.secondary.main} />
        <Typography variant="caption" fontWeight={600} textTransform={'uppercase'}>
          Unit Ordered
        </Typography>
      </Stack>
    </Stack>
  );
}

export function DispensingTimeline({
  dispenseWindow,
  currentOrderQuantity,
}:
  | {
      dispenseWindow: PreviousDispenseWindow;
      currentOrderQuantity?: undefined;
    }
  | {
      dispenseWindow: CurrentDispenseWindow;
      currentOrderQuantity: number;
    }) {
  const theme = useTheme();

  // Returning early if no dispense window is given
  if (!dispenseWindow) {
    return (
      <Typography variant="caption" color="textSecondary">
        No dispensing history found
      </Typography>
    );
  }

  const startDate = parseIntervalDate(dispenseWindow.start);
  const endDate = parseIntervalDate(dispenseWindow.end);
  const windowHasNoPreviousDispensation = dispenseWindow.dispenses.length === 0;

  // Returning early for the first dispense of the current dispense window as no other points will need to be created
  if (windowHasNoPreviousDispensation && currentOrderQuantity) {
    return (
      <Stack direction={'column'} gap={1}>
        <Typography variant="caption" color="textSecondary">
          Not started
        </Typography>

        <Timeline
          startDate={startDate.isoString}
          endDate={endDate.isoString}
          points={[
            {
              formattedDate: startDate.formatted,
              isoDate: startDate.isoString,
              value: currentOrderQuantity,
              shouldStopProgress: true,
              label: (
                <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.success.dark }}>
                  Today
                </Typography>
              ),
            },
          ]}
        />
      </Stack>
    );
  }

  // Creating the dispense points for either the previous or current dispense window as the logic is the same
  let points: TimelinePoint[] = Object.values(
    dispenseWindow.dispenses.reduce<Record<string, { formattedDate: string; isoDate: string; value: number }>>(
      (acc, dispense) => {
        const parsedDispensedDate = parseIntervalDate(dispense.date);

        if (!acc[parsedDispensedDate.formatted]) {
          acc[parsedDispensedDate.formatted] = {
            formattedDate: parsedDispensedDate.formatted,
            isoDate: parsedDispensedDate.isoString,
            value: 0,
          };
        }

        acc[parsedDispensedDate.formatted].value += dispense.units;

        return acc;
      },
      {},
    ),
  );

  // Adding in the current order for "today" if we are creating a "current" dispense window, this is ignored for the previous dispense window
  if (currentOrderQuantity) {
    // HACK: Hardcoding now to DISPENSE_INTERVAL_FALLBACK_TZ here, to remove this we would need to know the current order date or the current date as a fixed offset timezone iso date OR we would need to know the IANA zone name that was used to create the fixed offset timezone iso dates for the related dispense window
    const nowAsDispenseIntervalFallbackTZ = DateTime.now().setZone(DISPENSE_INTERVAL_FALLBACK_TZ);
    if (!nowAsDispenseIntervalFallbackTZ.isValid) {
      return null;
    }

    const parsedNow = parseIntervalDate(nowAsDispenseIntervalFallbackTZ.toISO());

    points = [
      ...points,
      {
        formattedDate: parsedNow.formatted,
        isoDate: parsedNow.isoString,
        value: currentOrderQuantity,
        shouldStopProgress: true,
        label: (
          <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.success.dark }}>
            Today
          </Typography>
        ),
      },
    ];
  }

  return (
    <Stack direction={'column'} gap={1}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant="caption" color="textSecondary">
          {startDate.formatted}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {endDate.formatted}
        </Typography>
      </Stack>

      <Timeline startDate={startDate.isoString} endDate={endDate.isoString} points={points} />
    </Stack>
  );
}
